import { useCallback, useState, useMemo } from "react";
import { authenticatedFetch } from "../lib/api";
// ENG-2648: attach the backend's error reference so a customer can quote it.
import { errorWithRef } from "../lib/apiError";
import { SyncResult } from "../lib/directoryStatus";
import { notifyOrgPolicyChanged } from "../lib/orgPolicyBroadcast";

/**
 * ENG-3366: a sync that moved anyone in or out of a group changed those members' allow lists and
 * enforcement, so it announces a policy change the way a list write does. Whatever the status: a sync
 * can fail part-way after writing, and its counts say what it wrote. Never for a preview.
 */
const announceMembershipChanges = (result: SyncResult): SyncResult => {
  if (!result.dryRun && (result.membersAdded > 0 || result.membersRemoved > 0)) notifyOrgPolicyChanged();
  return result;
};

// ENG-2586: the tenant's external directory connection (Google Workspace today, Entra later).
//
// Every fetch gates on its own *Loaded flag and sets its error on failure while leaving the flag
// false (ENG-2591/2595): an empty group list must never be indistinguishable from a 403 or a dead
// connection, because "you have no Workspace groups" and "we couldn't ask Google" lead the admin to
// opposite conclusions.

/** `GET /business/directory` — never carries the OAuth token, only connection metadata. */
export interface DirectoryConnection {
  connected: boolean;
  provider?: string | null;
  adminEmail?: string | null;
  verifiedDomain?: string | null;
  /** `connected` | `revoked` | … — distinct from the boolean, see `needsReconnect`. */
  status?: string | null;
  lastSyncedAt?: string | null;
  /**
   * Connected, but another tenant already holds the verified claim on this domain, so the domain
   * write-back was refused. Groups still list and sync; domain-scoped enrollment does not belong to
   * this tenant. Recomputed server-side per read, since the claim can move.
   */
  domainClaimedElsewhere?: boolean;
  /** ENG-3457: this tenant holds the verified claim on [verifiedDomain], which alone proves nothing. */
  domainVerified?: boolean;
  /** ENG-3457: coworkers on the verified domain join by themselves (a staff-set switch). */
  autoEnroll?: boolean;
}

/** One group as Google sees it (`GET /business/directory/groups`). */
export interface DirectoryGroup {
  externalId: string;
  email: string;
  name: string;
  memberCount?: number | null;
}

/** A binding of a directory group to a Haven group (`/business/groups/{id}/external-refs`). */
export interface ExternalRef {
  id: string;
  provider: string;
  externalId: string;
}

/**
 * `POST /business/directory/groups/{id}/sync`, apply or preview (ENG-2651, ENG-3346). Returns the body
 * on every documented status, like `syncNow`: the failures carry a `DirectorySyncResponse` too, and
 * they are the informative ones.
 */
async function postGroupSync(groupId: string, query: string, fallback: string, dryRun: boolean): Promise<SyncResult> {
  // A result built HERE, not by the server, still has to say whether it came from a preview — the
  // page reads `dryRun` to decide what "try again" means.
  const failed = (message: string): SyncResult => ({
    status: "ERROR",
    groupsSynced: 0,
    membersAdded: 0,
    membersRemoved: 0,
    unknownEmails: [],
    message,
    dryRun,
  });
  try {
    const response = await authenticatedFetch(`/business/directory/groups/${groupId}/sync${query}`, { method: "POST" });
    const data = await response.json().catch(() => ({}));
    if (typeof data.status !== "string") return failed(errorWithRef(data, fallback));
    const result = data as SyncResult;
    // A preview is only a preview if the server says so. A backend from before ENG-3346 ignores
    // `?dryRun` and runs the real per-group sync, answering like any clean run (an unmapped group
    // reports 0 added, 0 removed). Taken as a plan, that would confirm "no one is removed" and then
    // remove people on Apply. The console and the backend reach prod through separate gates, so
    // this is refused here rather than trusted to the deploy order.
    if (dryRun && result.dryRun !== true) {
      return failed(
        "Couldn't preview that change, so it wasn't made. Haven's server needs an update before a " +
          "mapping change can be previewed. Try again later.",
      );
    }
    // Same defaulting as syncNow: the backend's Json omits fields left at their default, so a clean
    // run sends none of these arrays rather than sending them empty.
    return announceMembershipChanges({
      ...result,
      unknownEmails: result.unknownEmails ?? [],
      added: result.added ?? [],
      removed: result.removed ?? [],
      dryRun,
    });
  } catch (err: unknown) {
    return failed(err instanceof Error ? err.message : fallback);
  }
}

export function useDirectory() {
  const [connection, setConnection] = useState<DirectoryConnection | null>(null);
  const [connectionLoaded, setConnectionLoaded] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [directoryError, setDirectoryErrorMessage] = useState<string | null>(null);
  /**
   * The backend's machine-readable `code` for the current error, when it sent one (ENG-2638).
   *
   * Kept beside the message rather than folded into it because the message is prose and the branch
   * is a decision: `/business/directory/groups` answers 403 both when the CALLER isn't a Haven admin
   * and when the connected GOOGLE account can't read the directory, and those want opposite UI.
   * Null means "no code" — an older backend, or a failure that never reached one.
   */
  const [directoryErrorCode, setDirectoryErrorCode] = useState<string | null>(null);

  /**
   * Set (or clear) the error message, always dropping any code with it.
   *
   * The pairing is the point: a code left behind by a previous failure would render a confident,
   * specific panel about a problem that is no longer the one occurring — "ask your Workspace admin
   * for the Groups Reader role" on top of a network timeout. Callers can't forget to clear it
   * because they have no way to reach the code at all.
   */
  const setDirectoryError = useCallback((message: string | null) => {
    setDirectoryErrorMessage(message);
    setDirectoryErrorCode(null);
  }, []);

  /** Record a failure the backend classified, keeping message and code in step. */
  const setClassifiedError = useCallback((message: string, code: string | null) => {
    setDirectoryErrorMessage(message);
    setDirectoryErrorCode(code ?? null);
  }, []);

  const [directoryGroups, setDirectoryGroups] = useState<DirectoryGroup[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);

  /** Haven group id → its bindings. Populated by [fetchRefsFor]; absent means "not looked up yet". */
  const [refsByGroup, setRefsByGroup] = useState<Record<string, ExternalRef[]>>({});

  const fetchConnection = useCallback(async () => {
    setConnectionLoading(true);
    try {
      const response = await authenticatedFetch("/business/directory");
      if (response.ok) {
        setConnection((await response.json()) as DirectoryConnection);
        setConnectionLoaded(true);
        setDirectoryError(null);
      } else if (response.status === 503) {
        // Not configured in this environment (alpha/dev keep the feature dormant). A real answer,
        // not a failure — record it as a disconnected connection so the page can say so precisely.
        setConnection({ connected: false, status: "not_configured" });
        setConnectionLoaded(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setDirectoryError(errorWithRef(data, "Couldn't load the directory connection"));
      }
    } catch (err: unknown) {
      setDirectoryError(err instanceof Error ? err.message : "Couldn't load the directory connection");
    } finally {
      setConnectionLoading(false);
    }
    // Stable identity: DirectoryPage and GroupsPage key load effects on these. Rebuilt every render,
    // they re-ran the effect every render — and because a failed load leaves its *Loaded flag false,
    // the guard never closed and the page hammered the endpoint in a loop (ENG-2586).
    // ENG-2722: the error helpers are `useCallback`s with empty dep lists, so naming them keeps this
    // identity fixed for the life of the hook, exactly as the empty list did.
  }, [setDirectoryError]);

  /** The Workspace's groups. Only meaningful while connected — the backend 409s once access is gone. */
  const fetchDirectoryGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const response = await authenticatedFetch("/business/directory/groups");
      if (response.ok) {
        setDirectoryGroups((await response.json()) as DirectoryGroup[]);
        setGroupsLoaded(true);
      } else {
        const data = await response.json().catch(() => ({}));
        // The `code` carries the distinction the status can't: 403 is BOTH "you're not a Haven
        // admin" and "the connected Google account can't read the directory", and 409 is both "no
        // Workspace connected" and "access revoked". Each pair needs opposite UI, so the code is
        // preserved for `describeDirectoryBlock` and the message kept only as the fallback for a
        // backend too old to send one.
        setClassifiedError(
          errorWithRef(data, "Couldn't list your Workspace groups"),
          typeof data.code === "string" ? data.code : null,
        );
      }
    } catch (err: unknown) {
      setDirectoryError(err instanceof Error ? err.message : "Couldn't list your Workspace groups");
    } finally {
      setGroupsLoading(false);
    }
    // ENG-2722: see fetchConnection above — both error helpers are permanently-stable useCallbacks.
  }, [setDirectoryError, setClassifiedError]);

  /** Start the consent round-trip. Returns the URL to send the browser to, or an error to show. */
  const startConnect = useCallback(async (): Promise<{ authorizeUrl?: string; error?: string }> => {
    try {
      const response = await authenticatedFetch("/business/directory/connect", { method: "POST" });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.authorizeUrl) return { authorizeUrl: data.authorizeUrl as string };
      return {
        error:
          response.status === 503
            ? "Directory connections aren't configured in this environment."
            : errorWithRef(data, "Couldn't start the connection"),
      };
    } catch (err: unknown) {
      return { error: err instanceof Error ? err.message : "Couldn't start the connection" };
    }
  }, []);

  const disconnect = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch("/business/directory", { method: "DELETE" });
      if (response.ok) {
        // Fail closed locally too: drop the cached directory groups, since nothing may list them now.
        setConnection({ connected: false });
        setDirectoryGroups([]);
        setGroupsLoaded(false);
        // The cached mappings drive the "Directory-owned" badge on the Groups page. Left behind, a
        // disconnected tenant kept claiming its groups were directory-managed — the one statement on
        // that page that had just stopped being true.
        setRefsByGroup({});
        // The error described a connection that no longer exists. Left standing, disconnecting to
        // escape a bad connection leaves its complaint on screen as though nothing happened.
        setDirectoryError(null);
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: errorWithRef(data, "Couldn't disconnect") };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Couldn't disconnect" };
    }
  }, [setDirectoryError]);

  /**
   * Run a reconcile now. Returns the body on EVERY documented status — 200/409/502/503 all carry a
   * `DirectorySyncResponse`, and the non-200s are the informative ones, so treating them as
   * exceptions would discard exactly the detail the admin needs.
   */
  const syncNow = useCallback(async (): Promise<SyncResult> => {
    try {
      const response = await authenticatedFetch("/business/directory/sync", { method: "POST" });
      const data = await response.json().catch(() => ({}));
      if (typeof data.status === "string") {
        // The backend's Json has encodeDefaults=false (the kotlinx default), so a field left at its
        // default is ABSENT from the payload, not null: a sync with nothing skipped omits
        // `unknownEmails` entirely. Reading `.length` off that undefined threw during render and the
        // error boundary swallowed the whole page. Normalise on the way in rather than defending at
        // every use site.
        const result: SyncResult = {
          ...(data as SyncResult),
          unknownEmails: (data as SyncResult).unknownEmails ?? [],
        };
        // A successful reconcile moves lastSyncedAt; re-read rather than guessing the timestamp.
        if (result.status === "SUCCESS") await fetchConnection();
        if (result.status === "AUTH_REVOKED") await fetchConnection();
        return announceMembershipChanges(result);
      }
      return {
        status: "ERROR",
        groupsSynced: 0,
        membersAdded: 0,
        membersRemoved: 0,
        unknownEmails: [],
        message: errorWithRef(data, "Sync failed"),
      };
    } catch (err: unknown) {
      return {
        status: "ERROR",
        groupsSynced: 0,
        membersAdded: 0,
        membersRemoved: 0,
        unknownEmails: [],
        message: err instanceof Error ? err.message : "Sync failed",
      };
    }
  }, [fetchConnection]);

  /**
   * Reconcile ONE Haven group (ENG-2651).
   *
   * Used to apply a mapping change immediately. Deliberately not `syncNow`: that reconciles every
   * mapped group in the tenant and removes members the directory no longer lists, so running it
   * after a single mapping edit could strip people from groups the admin never touched.
   */
  const syncGroup = useCallback(
    (groupId: string): Promise<SyncResult> => postGroupSync(groupId, "", "Couldn't sync that group", false),
    [],
  );

  /**
   * What `syncGroup` WOULD do, naming the people, without writing anything (ENG-3346).
   *
   * `change` previews a mapping edit that has not been made yet — which is the point: the console
   * confirms the effect BEFORE writing the mapping, since a written mapping is applied by the next
   * scheduled sync whether or not anyone approved it. Same endpoint as the apply, with `dryRun`.
   */
  const previewGroup = useCallback(
    (groupId: string, change: { addExternalId?: string; removeRefId?: string } = {}): Promise<SyncResult> => {
      const query = new URLSearchParams({ dryRun: "true" });
      if (change.addExternalId) query.set("addExternalId", change.addExternalId);
      if (change.removeRefId) query.set("removeRefId", change.removeRefId);
      return postGroupSync(groupId, `?${query}`, "Nothing was changed.", true);
    },
    [],
  );

  /**
   * Load every Haven group's directory bindings, in ONE request (ENG-2643).
   *
   * This was one request per group — the badge on the Groups page needs every group's state at once,
   * and there was no bulk read. That meant N authenticated requests per page load, each paying a
   * Firebase token validation, a tenant resolution and a query, all fired concurrently at a service
   * running maxScale=2. Bounded by group count, but the wrong shape.
   *
   * The parameter is kept so call sites don't change and so the effect can stay keyed on the group
   * ids; the ids are no longer sent, because the endpoint is tenant-scoped and returns the lot.
   *
   * Failure stays silent by design — a missing badge is a smaller lie than an error banner over the
   * whole page. It now fails as one request rather than per group, so the page is never left with a
   * half-populated map that looks authoritative.
   */
  const fetchRefsFor = useCallback(async (_groupIds?: string[]) => {
    try {
      const response = await authenticatedFetch("/business/directory/external-refs");
      if (!response.ok) return;
      // Replaces rather than merges: this is the complete set for the tenant, so keeping stale keys
      // would preserve a badge for a mapping that has since been removed.
      setRefsByGroup((await response.json()) as Record<string, ExternalRef[]>);
    } catch {
      // Intentionally silent; see above.
    }
  }, []);

  const addRef = useCallback(async (groupId: string, externalId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${groupId}/external-refs`, {
        method: "POST",
        body: JSON.stringify({ provider: "google", externalId }),
      });
      if (response.ok) {
        const ref = (await response.json()) as ExternalRef;
        setRefsByGroup((prev) => ({ ...prev, [groupId]: [...(prev[groupId] ?? []), ref] }));
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: errorWithRef(data, "Couldn't map that group") };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Couldn't map that group" };
    }
  }, []);

  const removeRef = useCallback(async (groupId: string, refId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${groupId}/external-refs/${refId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setRefsByGroup((prev) => ({
          ...prev,
          [groupId]: (prev[groupId] ?? []).filter((r) => r.id !== refId),
        }));
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: errorWithRef(data, "Couldn't remove that mapping") };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Couldn't remove that mapping" };
    }
  }, []);

  /**
   * ENG-3406: forget the organization that signed out. App holds this hook for the whole tab, and the
   * loaded flags stop DirectoryPage refetching, so without this the next admin to sign in here saw the
   * previous organization's Workspace connection and group mapping.
   */
  const reset = useCallback(() => {
    setConnection(null);
    setConnectionLoaded(false);
    setConnectionLoading(false);
    setDirectoryError(null);
    setDirectoryGroups([]);
    setGroupsLoaded(false);
    setGroupsLoading(false);
    setRefsByGroup({});
  }, [setDirectoryError]);

  // Memoised like the other org hooks (ENG-2722). GroupsPage and DirectoryPage key effects on
  // functions from here, and consumers take the whole object as `DirectoryApi`, so a fresh one per
  // render is what forced their suppressions.
  return useMemo(
    () => ({
      connection,
      connectionLoaded,
      connectionLoading,
      directoryError,
      directoryErrorCode,
      setDirectoryError,
      directoryGroups,
      groupsLoaded,
      groupsLoading,
      refsByGroup,
      fetchConnection,
      fetchDirectoryGroups,
      fetchRefsFor,
      startConnect,
      disconnect,
      syncNow,
      syncGroup,
      previewGroup,
      addRef,
      removeRef,
      reset,
    }),
    [connection, connectionLoaded, connectionLoading, directoryError, directoryErrorCode, setDirectoryError, directoryGroups, groupsLoaded, groupsLoading, refsByGroup, fetchConnection, fetchDirectoryGroups, fetchRefsFor, startConnect, disconnect, syncNow, syncGroup, previewGroup, addRef, removeRef, reset],
  );
}

export type DirectoryApi = ReturnType<typeof useDirectory>;
